import React, { useEffect, useState } from 'react';
import api from '../api';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { QRCodeSVG } from 'qrcode.react';

const UpdateAccount = () => {
    const [profile, setProfile] = useState({
        email: '',
        firstName: '',
        lastName: '',
        organization: '',
        role: '',
        status: '',
    });
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [profileSaving, setProfileSaving] = useState(false);
    const [passwordSaving, setPasswordSaving] = useState(false);

    const [profileMessage, setProfileMessage] = useState('');
    const [profileError, setProfileError] = useState('');

    const [passwordMessage, setPasswordMessage] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [mfaSetup, setMfaSetup] = useState(null);
    const [mfaCode, setMfaCode] = useState('');
    const [mfaDisablePassword, setMfaDisablePassword] = useState('');
    const [mfaLoading, setMfaLoading] = useState(false);
    const [mfaMessage, setMfaMessage] = useState('');
    const [mfaError, setMfaError] = useState('');
    const [activeTab, setActiveTab] = useState('security');

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await api.get('/admin/api/admin-users/me');
                if (!mounted) return;
                setProfile((prev) => ({
                    ...prev,
                    email: res.data?.email || '',
                    firstName: res.data?.firstName || '',
                    lastName: res.data?.lastName || '',
                    organization: res.data?.organization || '',
                    role: res.data?.role || '',
                    status: res.data?.status || '',
                    mfaEnabled: Boolean(res.data?.mfaEnabled),
                }));
            } catch (err) {
                if (mounted) {
                    const msg = err.response?.data?.message || err.response?.data || 'Failed to load account details';
                    setProfileError(typeof msg === 'string' ? msg : 'Failed to load account details');
                }
            } finally {
                if (mounted) setLoadingProfile(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const handleProfileChange = (field) => (event) => {
        setProfile((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleProfileSave = async (event) => {
        event.preventDefault();
        setProfileMessage('');
        setProfileError('');
        if (!profile.firstName.trim() || !profile.lastName.trim()) {
            setProfileError('First name and last name are required.');
            return;
        }
        try {
            setProfileSaving(true);
            await api.put('/admin/api/admin-users/me', {
                firstName: profile.firstName.trim(),
                lastName: profile.lastName.trim(),
                organization: profile.organization.trim(),
            });
            setProfileMessage('Account details updated.');
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data || 'Unable to update account details';
            setProfileError(typeof msg === 'string' ? msg : 'Unable to update account details');
        } finally {
            setProfileSaving(false);
        }
    };

    const handlePasswordChange = (field) => (event) => {
        setPasswordForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handlePasswordSave = async (event) => {
        event.preventDefault();
        setPasswordMessage('');
        setPasswordError('');
        if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
            setPasswordError('All password fields are required.');
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setPasswordError('New password and confirmation do not match.');
            return;
        }
        try {
            setPasswordSaving(true);
            await api.post('/admin/api/admin-users/me/change-password', {
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
            });
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setPasswordMessage('Password updated.');
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data || 'Unable to update password';
            setPasswordError(typeof msg === 'string' ? msg : 'Unable to update password');
        } finally {
            setPasswordSaving(false);
        }
    };

    const beginMfaSetup = async () => {
        setMfaError('');
        setMfaMessage('');
        try {
            setMfaLoading(true);
            const res = await api.post('/admin/api/admin-users/me/mfa/setup');
            setMfaSetup(res.data);
            setMfaCode('');
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data || 'Unable to start Microsoft Authenticator setup';
            setMfaError(typeof msg === 'string' ? msg : 'Unable to start Microsoft Authenticator setup');
        } finally {
            setMfaLoading(false);
        }
    };

    const enableMfa = async (event) => {
        event.preventDefault();
        setMfaError('');
        setMfaMessage('');
        if (!mfaSetup?.secret) {
            setMfaError('Start setup before verifying Microsoft Authenticator.');
            return;
        }
        if (!mfaCode.trim()) {
            setMfaError('Enter the 6-digit code from Microsoft Authenticator.');
            return;
        }
        try {
            setMfaLoading(true);
            await api.post('/admin/api/admin-users/me/mfa/enable', {
                secret: mfaSetup.secret,
                code: mfaCode.trim(),
            });
            setProfile((prev) => ({ ...prev, mfaEnabled: true }));
            setMfaSetup(null);
            setMfaCode('');
            setMfaMessage('Microsoft Authenticator is now enabled for sign-in.');
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data || 'Unable to verify Microsoft Authenticator';
            setMfaError(typeof msg === 'string' ? msg : 'Unable to verify Microsoft Authenticator');
        } finally {
            setMfaLoading(false);
        }
    };

    const disableMfa = async (event) => {
        event.preventDefault();
        setMfaError('');
        setMfaMessage('');
        if (!mfaDisablePassword) {
            setMfaError('Enter your current password to disable Microsoft Authenticator.');
            return;
        }
        try {
            setMfaLoading(true);
            await api.post('/admin/api/admin-users/me/mfa/disable', {
                currentPassword: mfaDisablePassword,
            });
            setProfile((prev) => ({ ...prev, mfaEnabled: false }));
            setMfaDisablePassword('');
            setMfaSetup(null);
            setMfaCode('');
            setMfaMessage('Microsoft Authenticator has been disabled.');
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data || 'Unable to disable Microsoft Authenticator';
            setMfaError(typeof msg === 'string' ? msg : 'Unable to disable Microsoft Authenticator');
        } finally {
            setMfaLoading(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 920, mx: 'auto', my: 4 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                Update Account
            </Typography>

            {loadingProfile ? (
                <Typography>Loading account details...</Typography>
            ) : (
                <Paper elevation={1}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, value) => setActiveTab(value)}
                        variant="fullWidth"
                        sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}
                    >
                        <Tab label="Security" value="security" />
                        <Tab label="Account" value="account" />
                    </Tabs>
                    <Divider />

                    {activeTab === 'security' ? (
                        <Box sx={{ p: 3 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3 }} elevation={0} variant="outlined">
                                        <Typography variant="h6" sx={{ mb: 2 }}>
                                            Change Password
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        {passwordError ? <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert> : null}
                                        {passwordMessage ? <Alert severity="success" sx={{ mb: 2 }}>{passwordMessage}</Alert> : null}
                                        <Box component="form" onSubmit={handlePasswordSave}>
                                            <TextField
                                                fullWidth
                                                label="Current Password"
                                                type="password"
                                                value={passwordForm.currentPassword}
                                                onChange={handlePasswordChange('currentPassword')}
                                                margin="normal"
                                                required
                                            />
                                            <TextField
                                                fullWidth
                                                label="New Password"
                                                type="password"
                                                value={passwordForm.newPassword}
                                                onChange={handlePasswordChange('newPassword')}
                                                margin="normal"
                                                required
                                            />
                                            <TextField
                                                fullWidth
                                                label="Confirm New Password"
                                                type="password"
                                                value={passwordForm.confirmPassword}
                                                onChange={handlePasswordChange('confirmPassword')}
                                                margin="normal"
                                                required
                                            />
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={passwordSaving}
                                                sx={{ mt: 2 }}
                                            >
                                                Update Password
                                            </Button>
                                        </Box>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3 }} elevation={0} variant="outlined">
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
                                            <Box>
                                                <Typography variant="h6">Microsoft Authenticator</Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                    Use the Microsoft Authenticator app for a 6-digit sign-in code after your password.
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={profile.mfaEnabled ? 'Enabled' : 'Not Enabled'}
                                                color={profile.mfaEnabled ? 'success' : 'default'}
                                                variant={profile.mfaEnabled ? 'filled' : 'outlined'}
                                                sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                                            />
                                        </Stack>
                                        <Divider sx={{ mb: 2 }} />
                                        {mfaError ? <Alert severity="error" sx={{ mb: 2 }}>{mfaError}</Alert> : null}
                                        {mfaMessage ? <Alert severity="success" sx={{ mb: 2 }}>{mfaMessage}</Alert> : null}

                                        {!profile.mfaEnabled && !mfaSetup && (
                                            <Box>
                                                <Typography sx={{ mb: 2 }}>
                                                    Click below to generate a QR code, then scan it with Microsoft Authenticator and verify the 6-digit code.
                                                </Typography>
                                                <Button variant="contained" onClick={beginMfaSetup} disabled={mfaLoading}>
                                                    Set Up Microsoft Authenticator
                                                </Button>
                                            </Box>
                                        )}

                                        {!profile.mfaEnabled && mfaSetup && (
                                            <Grid container spacing={3} alignItems="flex-start">
                                                <Grid item xs={12} md={4}>
                                                    <Paper variant="outlined" sx={{ p: 2, display: 'inline-flex', backgroundColor: '#fff' }}>
                                                        <QRCodeSVG value={mfaSetup.provisioningUri} size={180} includeMargin />
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={12} md={8}>
                                                    <Typography sx={{ mb: 1 }}>
                                                        1. Open Microsoft Authenticator.
                                                    </Typography>
                                                    <Typography sx={{ mb: 1 }}>
                                                        2. Add a new account and scan this QR code.
                                                    </Typography>
                                                    <Typography sx={{ mb: 2 }}>
                                                        3. Enter the 6-digit code shown in the app to finish setup.
                                                    </Typography>
                                                    <Alert severity="info" sx={{ mb: 2 }}>
                                                        If scanning does not work, enter this setup key manually: <strong>{mfaSetup.secret}</strong>
                                                    </Alert>
                                                    <Box component="form" onSubmit={enableMfa}>
                                                        <TextField
                                                            fullWidth
                                                            label="Authenticator Code"
                                                            value={mfaCode}
                                                            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                            margin="normal"
                                                            required
                                                            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                                                        />
                                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                                                            <Button type="submit" variant="contained" disabled={mfaLoading}>
                                                                Verify and Enable
                                                            </Button>
                                                            <Button variant="outlined" disabled={mfaLoading} onClick={() => { setMfaSetup(null); setMfaCode(''); setMfaError(''); }}>
                                                                Cancel Setup
                                                            </Button>
                                                        </Stack>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        )}

                                        {profile.mfaEnabled && (
                                            <Box component="form" onSubmit={disableMfa}>
                                                <Alert severity="success" sx={{ mb: 2 }}>
                                                    Microsoft Authenticator is currently required when you sign in to the admin dashboard.
                                                </Alert>
                                                <Typography sx={{ mb: 2 }}>
                                                    To turn it off, confirm your current password.
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    label="Current Password"
                                                    type="password"
                                                    value={mfaDisablePassword}
                                                    onChange={(e) => setMfaDisablePassword(e.target.value)}
                                                    margin="normal"
                                                    required
                                                />
                                                <Button type="submit" variant="outlined" color="error" disabled={mfaLoading} sx={{ mt: 2 }}>
                                                    Disable Microsoft Authenticator
                                                </Button>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    ) : (
                        <Box sx={{ p: 3 }}>
                            <Paper sx={{ p: 3 }} elevation={0} variant="outlined">
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Profile
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                {profileError ? <Alert severity="error" sx={{ mb: 2 }}>{profileError}</Alert> : null}
                                {profileMessage ? <Alert severity="success" sx={{ mb: 2 }}>{profileMessage}</Alert> : null}
                                <Box component="form" onSubmit={handleProfileSave}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        value={profile.email}
                                        margin="normal"
                                        disabled
                                    />
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        value={profile.firstName}
                                        onChange={handleProfileChange('firstName')}
                                        margin="normal"
                                        required
                                    />
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        value={profile.lastName}
                                        onChange={handleProfileChange('lastName')}
                                        margin="normal"
                                        required
                                    />
                                    <TextField
                                        fullWidth
                                        label="Organization"
                                        value={profile.organization}
                                        onChange={handleProfileChange('organization')}
                                        margin="normal"
                                    />
                                    <TextField
                                        fullWidth
                                        label="Role"
                                        value={profile.role}
                                        margin="normal"
                                        disabled
                                    />
                                    <TextField
                                        fullWidth
                                        label="Status"
                                        value={profile.status}
                                        margin="normal"
                                        disabled
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={profileSaving}
                                        sx={{ mt: 2 }}
                                    >
                                        Save Profile
                                    </Button>
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default UpdateAccount;
